import {
  Badge,
  Body1,
  Button,
  Field,
  Input,
  Spinner,
  Text,
  Title3,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import type { Department } from '@worklogix/shared';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { SHRINKABLE } from '../responsive';

import { useTimeAdminApi } from './api';
import { DataTable } from './components/DataTable';
import type { DataColumn } from './components/DataTable';
import { SideBlade } from './components/SideBlade';
import { toErrorMessage } from './errors';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    // Every ancestor between the page and a table's scroller must be able to
    // shrink, or the overflow lands on the document instead. See responsive.ts.
    ...SHRINKABLE,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalM,
    marginTop: tokens.spacingVerticalM,
  },
  banner: {
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  error: {
    color: tokens.colorPaletteRedForeground1,
  },
});

/** True when a department is mapped to a NetSuite department id. */
function isMatched(dept: Department): boolean {
  return dept.nsDepartmentId !== null && dept.nsDepartmentId !== '';
}

/**
 * Department mapping (Admin). Runs Dev-B's `syncDepartments` (name-match), then
 * lists WorkLogix departments with their `ns_department_id` match state and lets
 * an admin manually override an unmatched (or wrong) mapping. Manual overrides
 * exist because NS department names don't always match WorkLogix seed names.
 */
export function DepartmentMappingTab(): JSX.Element {
  const styles = useStyles();
  const api = useTimeAdminApi();

  const [departments, setDepartments] = useState<Department[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [syncing, setSyncing] = useState(false);
  const [syncMessage, setSyncMessage] = useState<string | null>(null);

  // Override blade.
  const [target, setTarget] = useState<Department | null>(null);
  const [nsId, setNsId] = useState('');
  const [saving, setSaving] = useState(false);
  const [bladeError, setBladeError] = useState<string | null>(null);

  const reload = useCallback(() => {
    let active = true;
    setLoading(true);
    setError(null);
    void api
      .listDepartments()
      .then((rows) => {
        if (active) {
          setDepartments(rows);
        }
      })
      .catch((err: unknown) => {
        if (active) {
          setError(toErrorMessage(err));
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });
    return () => {
      active = false;
    };
  }, [api]);

  useEffect(() => reload(), [reload]);

  const handleSync = useCallback(async () => {
    setSyncing(true);
    setSyncMessage(null);
    try {
      const res = await api.syncDepartments();
      const wlxLeft = res.unmatchedWlx.length;
      const nsLeft = res.unmatchedNs.length;
      const ambiguous = res.ambiguous.length;
      const ambiguousNote =
        ambiguous > 0
          ? ` ${ambiguous} ambiguous name${ambiguous === 1 ? '' : 's'} (matched more than one ` +
            `NetSuite department) left unmapped — map ${ambiguous === 1 ? 'it' : 'them'} explicitly.`
          : '';
      setSyncMessage(
        `Sync complete: ${res.matched} matched, ${wlxLeft} WorkLogix ` +
          `department${wlxLeft === 1 ? '' : 's'} still unmatched, ` +
          `${nsLeft} NetSuite department${nsLeft === 1 ? '' : 's'} with no WorkLogix match.` +
          ambiguousNote
      );
      reload();
    } catch (err: unknown) {
      setSyncMessage(toErrorMessage(err));
    } finally {
      setSyncing(false);
    }
  }, [api, reload]);

  const openOverride = useCallback((dept: Department) => {
    setTarget(dept);
    setNsId(dept.nsDepartmentId ?? '');
    setBladeError(null);
  }, []);

  const closeOverride = useCallback(() => {
    setTarget(null);
    setNsId('');
    setBladeError(null);
    setSaving(false);
  }, []);

  const handleSaveOverride = useCallback(async () => {
    if (target === null) {
      return;
    }
    setSaving(true);
    setBladeError(null);
    const trimmed = nsId.trim();
    const nextNsId = trimmed === '' ? null : trimmed;
    try {
      // Dev-B's PATCH returns `{ ok: true }` (no row), so reflect the saved value
      // optimistically into the local list rather than from a response body.
      await api.updateDepartmentMapping(target.id, { nsDepartmentId: nextNsId });
      setDepartments((prev) =>
        prev.map((d) => (d.id === target.id ? { ...d, nsDepartmentId: nextNsId } : d))
      );
      closeOverride();
    } catch (err: unknown) {
      setBladeError(toErrorMessage(err));
      setSaving(false);
    }
  }, [api, target, nsId, closeOverride]);

  const matchedCount = useMemo(() => departments.filter(isMatched).length, [departments]);
  const unmatchedCount = departments.length - matchedCount;

  const columns: ReadonlyArray<DataColumn<Department>> = useMemo(
    () => [
      { columnId: 'name', header: 'Department', renderCell: (d) => d.name },
      {
        columnId: 'nsId',
        header: 'NetSuite department id',
        renderCell: (d) => (isMatched(d) ? (d.nsDepartmentId ?? '—') : '—'),
      },
      {
        columnId: 'match',
        header: 'Match',
        renderCell: (d) => (
          <Badge appearance="tint" color={isMatched(d) ? 'success' : 'warning'}>
            {isMatched(d) ? 'Matched' : 'Unmatched'}
          </Badge>
        ),
      },
      {
        columnId: 'action',
        header: '',
        renderCell: (d) => (
          <Button size="small" onClick={() => openOverride(d)}>
            {isMatched(d) ? 'Edit' : 'Map'}
          </Button>
        ),
      },
    ],
    [openOverride]
  );

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerText}>
          <Title3>Department mapping</Title3>
          <Body1>
            {departments.length} department{departments.length === 1 ? '' : 's'} · {matchedCount}{' '}
            matched · {unmatchedCount} unmatched
          </Body1>
        </div>
        <Button appearance="primary" onClick={() => void handleSync()} disabled={syncing}>
          {syncing ? <Spinner size="tiny" /> : 'Sync from NetSuite'}
        </Button>
      </div>

      {syncMessage !== null ? (
        <div className={styles.banner} role="status">
          <Text>{syncMessage}</Text>
        </div>
      ) : null}

      {error !== null ? (
        <Text className={styles.error} role="alert">
          {error}
        </Text>
      ) : null}

      {loading ? (
        <Spinner label="Loading departments…" />
      ) : (
        <DataTable
          columns={columns}
          rows={departments}
          getRowId={(d) => d.id}
          emptyMessage="No departments yet."
        />
      )}

      <SideBlade
        open={target !== null}
        onClose={closeOverride}
        title={target === null ? '' : `Map department — ${target.name}`}
        size="small"
      >
        {target !== null ? (
          <form
            className={styles.form}
            onSubmit={(ev) => {
              ev.preventDefault();
              void handleSaveOverride();
            }}
          >
            <Field
              label="NetSuite department id"
              hint="Enter the NS department internal id, or leave blank to clear the mapping."
            >
              <Input value={nsId} onChange={(_, d) => setNsId(d.value)} disabled={saving} />
            </Field>
            {bladeError !== null ? (
              <Text className={styles.error} role="alert">
                {bladeError}
              </Text>
            ) : null}
            <div className={styles.actions}>
              <Button appearance="secondary" onClick={closeOverride} disabled={saving}>
                Cancel
              </Button>
              <Button appearance="primary" type="submit" disabled={saving}>
                {saving ? <Spinner size="tiny" /> : 'Save mapping'}
              </Button>
            </div>
          </form>
        ) : null}
      </SideBlade>
    </div>
  );
}
